import { useEffect, useMemo, useState } from 'react';
import { useCharacterStore } from '@/state/characterStore';
import { useUIStore } from '@/state/uiStore';
import {
  listDestinations, teleportCost, useTeleportStore,
  type TeleportDest, type MapType,
  getTownScrollCooldownMs,
} from '@/state/teleportStore';
import { ui } from './theme';
import { CloseButton } from './CloseButton';

type Tab = 'all' | 'town' | 'field' | 'dungeon' | 'event';

export function TeleportPanel() {
  const ch = useCharacterStore((s) => s.current);
  const zen = useCharacterStore((s) => s.zen);
  const close = () => useUIStore.getState().set('teleport', false);

  const visited = useTeleportStore((s) => s.visitedMaps);
  const channeling = useTeleportStore((s) => s.channeling);
  const townScrollEndsAt = useTeleportStore((s) => s.townScrollEndsAt);
  const requestTeleport = useTeleportStore((s) => s.requestTeleport);
  const useTownScroll = useTeleportStore((s) => s.useTownScroll);

  const [tab, setTab] = useState<Tab>('all');
  const [selected, setSelected] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(id);
  }, []);

  const allDest = useMemo(() => listDestinations(), []);
  const filtered = useMemo(() => {
    if (tab === 'all') return allDest;
    return allDest.filter((d) => d.type === (tab as MapType));
  }, [allDest, tab]);

  const sel = useMemo(() => allDest.find((d) => d.mapId === selected) ?? null, [allDest, selected]);

  if (!ch) return null;

  const tsLeft = Math.max(0, townScrollEndsAt - now);
  const tsReady = tsLeft <= 0;
  const channelLeft = channeling ? Math.max(0, channeling.endsAt - now) : 0;

  const tabs: Array<[Tab, string]> = [
    ['all', 'Tất cả'], ['town', 'Town'], ['field', 'Field'],
    ['dungeon', 'Dungeon'], ['event', 'Event'],
  ];

  return (
    <div style={{
      position: 'absolute', top: 70, left: '50%', transform: 'translateX(-50%)',
      width: 620, maxWidth: '92vw', maxHeight: '80vh', display: 'flex', flexDirection: 'column',
      ...ui.panel,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <strong>🌀 Teleport Panel</strong>
        <CloseButton onClick={close} />
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, opacity: 0.85, marginBottom: 8 }}>
        <span>Hiện tại: <b>Map #{ch.mapId}</b></span>
        <span style={{ color: '#ffeb3b' }}>Z {zen.toLocaleString()}</span>
        <span>{tsReady ? 'Town Scroll: Sẵn sàng' : `Town Scroll: ${Math.ceil(tsLeft / 1000)}s`}</span>
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
        {tabs.map(([k, label]) => (
          <button key={k}
            style={{
              ...ui.secondaryBtn,
              padding: '4px 10px',
              opacity: tab === k ? 1 : 0.6,
              borderColor: tab === k ? '#ffd166' : '#4cd1ff',
            }}
            onClick={() => setTab(k)}
          >{label}</button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 10, minHeight: 0, flex: 1 }}>
        <div style={{ flex: '0 0 280px', overflowY: 'auto', border: '1px solid #1f2a3a', borderRadius: 4 }}>
          {filtered.map((d) => {
            const cost = teleportCost(d);
            const lockedLevel = ch.level < d.minLevel;
            const lockedVisit = !visited.has(d.mapId);
            const isCurrent = ch.mapId === d.mapId;
            const lock = lockedLevel || lockedVisit || isCurrent;
            const isSel = selected === d.mapId;
            return (
              <div key={d.mapId}
                onClick={() => setSelected(d.mapId)}
                style={{
                  padding: '6px 8px',
                  cursor: 'pointer',
                  background: isSel ? 'rgba(76,209,255,0.15)' : isCurrent ? 'rgba(255,209,102,0.10)' : 'transparent',
                  borderBottom: '1px solid #1f2a3a',
                  opacity: lock && !isCurrent ? 0.55 : 1,
                  display: 'flex', flexDirection: 'column', gap: 2,
                }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: 600 }}>
                    {visited.has(d.mapId) ? '✓' : '✗'} {d.name}
                  </span>
                  <span style={{ fontSize: 11, opacity: 0.7 }}>Lv {d.minLevel}+ · {d.type}</span>
                </div>
                <div style={{ fontSize: 11, color: '#ffeb3b' }}>
                  {isCurrent ? 'Đang ở đây'
                    : lockedLevel ? `Yêu cầu Lv ${d.minLevel}`
                    : lockedVisit ? 'Chưa khám phá'
                    : `${cost.toLocaleString()} Z`}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
          {sel ? (
            <>
              <div style={{
                height: 144, borderRadius: 4,
                background: 'linear-gradient(135deg,#1a2233,#0d1421)',
                border: '1px solid #324a5e',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#4cd1ff', fontSize: 28, fontWeight: 700,
              }}>
                {sel.name}
              </div>
              <div style={{ fontSize: 12, opacity: 0.85 }}>{sel.desc}</div>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {sel.highlights.map((h) => (
                  <span key={h} style={{
                    fontSize: 11, padding: '2px 6px', borderRadius: 10,
                    background: '#1a2740', border: '1px solid #324a5e',
                  }}>{h}</span>
                ))}
              </div>
              <div style={{ marginTop: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
                <button style={{ ...ui.secondaryBtn, opacity: tsReady ? 1 : 0.5 }}
                  disabled={!tsReady || !!channeling}
                  onClick={useTownScroll}>
                  📜 Town Scroll
                </button>
                <span style={{ flex: 1 }} />
                <button style={ui.secondaryBtn} onClick={close}>Hủy</button>
                <button
                  style={{ ...ui.primaryBtn, opacity: canTeleport(sel, ch.level, zen, visited.has(sel.mapId), ch.mapId, !!channeling) ? 1 : 0.5 }}
                  disabled={!canTeleport(sel, ch.level, zen, visited.has(sel.mapId), ch.mapId, !!channeling)}
                  onClick={() => requestTeleport(sel.mapId)}>
                  {channeling && channeling.mapId === sel.mapId
                    ? `Đang dịch chuyển... ${Math.ceil(channelLeft / 1000)}s`
                    : `Teleport (${teleportCost(sel).toLocaleString()} Z)`}
                </button>
              </div>
            </>
          ) : (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.5 }}>
              Chọn map để xem chi tiết
            </div>
          )}
        </div>
      </div>

      <div style={{ marginTop: 6, fontSize: 10, opacity: 0.5 }}>
        Town Scroll cooldown: {Math.round(getTownScrollCooldownMs() / 60000)} phút · Channel: 3 giây
      </div>
    </div>
  );
}

function canTeleport(d: TeleportDest, lv: number, zen: number, visited: boolean, curMap: number, channeling: boolean) {
  if (channeling) return false;
  if (curMap === d.mapId) return false;
  if (lv < d.minLevel) return false;
  if (!visited) return false;
  if (zen < teleportCost(d)) return false;
  return true;
}
